import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';

import { FSCollections } from '@src/constants/database';
import { db } from '@src/firebase/firebase';
import type {
  ArchiveOptionListParams,
  CreateOptionListParams,
  CreateOptionListResponse,
  OptionListChunkDTO,
  OptionListDTO,
  OptionListVersionResponse,
  UpdateOptionListParams,
} from '@src/models/interfaces/optionList.interface';
import { apiCallable } from '@src/util/cloud-functions/apiClient';
import { API_OPERATIONS } from '@src/util/cloud-functions/operations';

/**
 * Org lists live in `organizations/{org}/option_lists`; division users read the mirror the server
 * keeps under `divisions/{div}/option_lists` for lists their forms use. Writes go through the
 * `optionList` API operations only.
 */
class OptionListRepository {
  private listPath(organizationId: string, divisionId?: string): string[] {
    return divisionId
      ? [FSCollections.ORGANIZATIONS, organizationId, FSCollections.DIVISIONS, divisionId, FSCollections.OPTION_LISTS]
      : [FSCollections.ORGANIZATIONS, organizationId, FSCollections.OPTION_LISTS];
  }

  /** Every list of the organization; org readers only (Configuración › Listas). */
  async listAll(organizationId: string): Promise<OptionListDTO[]> {
    const [root, ...segments] = this.listPath(organizationId);
    const snapshot = await getDocs(collection(db, root, ...segments));
    return snapshot.docs.map(snap => ({ ...(snap.data() as OptionListDTO), id: snap.id }));
  }

  /**
   * Organization-wide lists plus the lists owned by `divisionIds`: the lists a form scoped to
   * those divisions may use. One query per value, since the rules only allow filtered reads to
   * users without an org reader role.
   */
  async listForDivisions(organizationId: string, divisionIds: string[]): Promise<OptionListDTO[]> {
    const [root, ...segments] = this.listPath(organizationId);
    const lists = collection(db, root, ...segments);
    const owners: (string | null)[] = [null, ...Array.from(new Set(divisionIds))];
    const snapshots = await Promise.all(owners.map(owner => getDocs(query(lists, where('divisionId', '==', owner)))));
    return snapshots.flatMap(snapshot =>
      snapshot.docs.map(snap => ({ ...(snap.data() as OptionListDTO), id: snap.id })),
    );
  }

  /** Reads a list and its chunks, from the division mirror when `divisionId` is given. */
  async get(
    organizationId: string,
    listId: string,
    divisionId?: string,
  ): Promise<{ list: OptionListDTO; chunks: OptionListChunkDTO[] } | null> {
    const [root, ...segments] = this.listPath(organizationId, divisionId);
    const listRef = doc(db, root, ...segments, listId);
    const [listSnap, chunksSnap] = await Promise.all([
      getDoc(listRef),
      getDocs(collection(listRef, FSCollections.OPTION_LIST_CHUNKS)),
    ]);
    if (!listSnap.exists()) return null;
    return {
      list: { ...(listSnap.data() as OptionListDTO), id: listSnap.id },
      chunks: chunksSnap.docs.map(snap => snap.data() as OptionListChunkDTO),
    };
  }

  async create(params: CreateOptionListParams): Promise<string> {
    const createFn = apiCallable<CreateOptionListParams, CreateOptionListResponse>(
      API_OPERATIONS.CREATE_OPTION_LIST,
    );
    const response = await createFn(params);
    return response.data.listId;
  }

  async update(params: UpdateOptionListParams): Promise<number> {
    const updateFn = apiCallable<UpdateOptionListParams, OptionListVersionResponse>(
      API_OPERATIONS.UPDATE_OPTION_LIST,
    );
    const response = await updateFn(params);
    return response.data.version;
  }

  async archive(params: ArchiveOptionListParams): Promise<number> {
    const archiveFn = apiCallable<ArchiveOptionListParams, OptionListVersionResponse>(
      API_OPERATIONS.ARCHIVE_OPTION_LIST,
    );
    const response = await archiveFn(params);
    return response.data.version;
  }
}

export default OptionListRepository;
